import { Label } from "@/components/ui/label";
import { useAppSelector } from "@/store/hooks";
import { useEffect, type Dispatch, type SetStateAction } from "react";
import { CustomSelect } from "../shared/CustomSelect";
import { Input } from "../ui/input";
import type { PersonalKycFormData } from "@/interfaces/kyc";
import type { UserProfile } from "@/interfaces/profile";
import type { Country } from "@/interfaces/country";
import { parsePhoneNumberFromString } from "libphonenumber-js";

interface Props {
  profile: NoInfer<UserProfile> | undefined;
  countries: NoInfer<Country[]> | undefined;
  isLevelInputLocked: boolean;
  personalKyc: PersonalKycFormData;
  setPersonalKyc: Dispatch<SetStateAction<PersonalKycFormData>>;
}

const personalKyc = ({
  profile,
  countries,
  isLevelInputLocked,
  personalKyc,
  setPersonalKyc,
}: Props) => {
  const { user } = useAppSelector((state) => state.auth);
  const userId = user?.id ?? "";

  const fullName = `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim();

  const phoneNumber = profile?.phoneNumber || "";
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 120 }, (_, i) => currentYear - i);

  console.log("ERSONAL INFO", personalKyc);

  useEffect(() => {
    if (!profile) return;

    const profilePhone = profile.phoneNumber ?? "";
    const parsedPhone = parsePhoneNumberFromString(profilePhone);

    setPersonalKyc((prev) => ({
      ...prev,
      fullName: `${profile.firstName ?? ""} ${profile.lastName ?? ""}`.trim(),
      phoneNumber: parsedPhone?.nationalNumber ?? "",
      dialCode: parsedPhone?.countryCallingCode
        ? `+${parsedPhone.countryCallingCode}`
        : prev.dialCode,
    }));
  }, [profile, setPersonalKyc]);

  const handleCountrySelect = (option: any) => {
    if (isLevelInputLocked) return;

    const country = countries?.find((c: any) => c.id === option?.value);

    setPersonalKyc((prev) => ({
      ...prev,
      country: option?.value ?? "",
      dialCode: country?.dialCode ?? "",
      phoneNumber: "",
    }));
  };

  const handleNationalitySelect = (option: any) => {
    if (isLevelInputLocked) return;

    setPersonalKyc((prev) => ({
      ...prev,
      nationality: option?.value ?? "",
    }));
  };

  const options = countries?.map((country: any) => {
    return {
      value: country.id,
      label: country.name,
      iso2: country.iso2,
    };
  });

  const formatOptionLabel = (option: any) => {
    const iso = option.iso2?.toLowerCase();

    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <img
          src={`https://flagcdn.com/w40/${iso}.png`}
          alt={option.label}
          width={20}
          height={14}
          style={{ objectFit: "cover", borderRadius: 2 }}
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = "none";
          }}
        />
        <span>{option.label}</span>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label className="mb-2">
            Full Name <span className="text-red-500">*</span>
          </Label>
          <div className="h-11 flex items-center rounded-md border bg-muted/40 px-3 text-sm text-muted-foreground">
            {fullName}
          </div>
        </div>
        <div>
          <Label className="mb-2">
            Date of Birth <span className="text-red-500">*</span>
          </Label>

          <div className="grid grid-cols-3 gap-2">
            {/* Year */}
            <select
              className="!h-12 px-3 rounded-md border bg-background"
              value={
                personalKyc.dateOfBirth
                  ? new Date(personalKyc.dateOfBirth).getFullYear()
                  : ""
              }
              onChange={(e) => {
                if (isLevelInputLocked) return;

                const year = Number(e.target.value);
                const current = personalKyc.dateOfBirth
                  ? new Date(personalKyc.dateOfBirth)
                  : new Date();

                const updated = new Date(
                  year,
                  current.getMonth(),
                  current.getDate(),
                );

                setPersonalKyc((prev) => ({
                  ...prev,
                  dateOfBirth: updated.toISOString().split("T")[0],
                }));
              }}
              disabled={isLevelInputLocked}
            >
              <option value="">Year</option>
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>

            {/* Month */}
            <select
              className="!h-12 px-3 rounded-md border bg-background"
              value={
                personalKyc.dateOfBirth
                  ? new Date(personalKyc.dateOfBirth).getMonth()
                  : ""
              }
              onChange={(e) => {
                if (isLevelInputLocked) return;

                const month = Number(e.target.value);
                const current = personalKyc.dateOfBirth
                  ? new Date(personalKyc.dateOfBirth)
                  : new Date();

                const updated = new Date(
                  current.getFullYear(),
                  month,
                  current.getDate(),
                );

                setPersonalKyc((prev) => ({
                  ...prev,
                  dateOfBirth: updated.toISOString().split("T")[0],
                }));
              }}
              disabled={isLevelInputLocked}
            >
              <option value="">Month</option>
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i} value={i}>
                  {new Date(0, i).toLocaleString("default", {
                    month: "long",
                  })}
                </option>
              ))}
            </select>

            {/* Day */}
            <select
              className="!h-12 px-3 rounded-md border bg-background"
              value={
                personalKyc.dateOfBirth
                  ? new Date(personalKyc.dateOfBirth).getDate()
                  : ""
              }
              onChange={(e) => {
                if (isLevelInputLocked) return;

                const day = Number(e.target.value);
                const current = personalKyc.dateOfBirth
                  ? new Date(personalKyc.dateOfBirth)
                  : new Date();

                const updated = new Date(
                  current.getFullYear(),
                  current.getMonth(),
                  day,
                );

                setPersonalKyc((prev) => ({
                  ...prev,
                  dateOfBirth: updated.toISOString().split("T")[0],
                }));
              }}
              disabled={isLevelInputLocked}
            >
              <option value="">Day</option>
              {Array.from({ length: 31 }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {i + 1}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label className="mb-2">Country of Residence</Label>
          <CustomSelect
            options={options}
            isClearable
            isSearchable
            isDisabled={isLevelInputLocked}
            value={
              options?.find((opt: any) => opt.value === personalKyc.country) ||
              null
            }
            onChange={handleCountrySelect}
            formatOptionLabel={formatOptionLabel}
            getOptionLabel={(option: any) => option.label}
            getOptionValue={(option: any) => option.value}
          />
        </div>

        <div>
          <Label className="mb-2">
            Phone Number <span className="text-red-500">*</span>
          </Label>

          {phoneNumber ? (
            <Input
              value={phoneNumber}
              disabled
              className="!h-12  bg-muted px-2"
              // placeholder="+code"
            />
          ) : (
            <div className="flex gap-2">
              {/* Dial Code (compact) */}
              <Input
                value={personalKyc.dialCode}
                disabled
                className="!h-12 w-[90px] md:w-[100px] bg-muted text-center px-2"
                placeholder="+code"
              />

              {/* Number (expands) */}
              <Input
                value={personalKyc.phoneNumber}
                onChange={(e) =>
                  setPersonalKyc((prev) => ({
                    ...prev,
                    phoneNumber: e.target.value.replace(/\D/g, ""),
                  }))
                }
                className="!h-12 flex-1 min-w-0 disabled:cursor-not-allowed"
                disabled={isLevelInputLocked || !personalKyc.country}
                placeholder="Phone number"
              />
            </div>
          )}
        </div>
      </div>
      <div className="w-full">
        <Label className="mb-2">
          Nationality <span className="text-red-500">*</span>
        </Label>
        <CustomSelect
          options={options}
          isClearable
          isSearchable
          isDisabled={isLevelInputLocked}
          value={
            options?.find(
              (opt: any) => opt.value === personalKyc.nationality,
            ) || null
          }
          onChange={handleNationalitySelect}
          formatOptionLabel={formatOptionLabel}
          getOptionLabel={(option: any) => option.label}
          getOptionValue={(option: any) => option.value}
        />
      </div>
      <div>
        <Label className="mb-2">
          Address <span className="text-red-500">*</span>
        </Label>
        <Input
          value={personalKyc.address}
          onChange={(e) =>
            setPersonalKyc({
              ...personalKyc,
              address: e.target.value,
            })
          }
          className="!h-12"
          placeholder="Street address"
          disabled={isLevelInputLocked}
          readOnly={isLevelInputLocked}
        />
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <div>
          <Label className="mb-2">City</Label>
          <Input
            value={personalKyc.city}
            onChange={(e) =>
              setPersonalKyc({
                ...personalKyc,
                city: e.target.value,
              })
            }
            className="!h-12"
            placeholder="City"
            disabled={isLevelInputLocked}
            readOnly={isLevelInputLocked}
          />
        </div>
        <div>
          <Label className="mb-2">State/Province</Label>
          <Input
            value={personalKyc.state}
            onChange={(e) =>
              setPersonalKyc({
                ...personalKyc,
                state: e.target.value,
              })
            }
            className="!h-12"
            placeholder="State"
            disabled={isLevelInputLocked}
            readOnly={isLevelInputLocked}
          />
        </div>
        <div>
          <Label className="mb-2">Postal Code</Label>
          <Input
            value={personalKyc.postalCode}
            onChange={(e) =>
              setPersonalKyc({
                ...personalKyc,
                postalCode: e.target.value,
              })
            }
            className="!h-12"
            placeholder="Postal code"
            disabled={isLevelInputLocked}
            readOnly={isLevelInputLocked}
          />
        </div>
      </div>
    </div>
  );
};

export default personalKyc;
